import { useEffect, useRef, useState } from 'react'
import { FormProvider, useFieldArray, useForm, type FieldErrors } from 'react-hook-form'
import { ArrowUUpLeftIcon, PlusCircleIcon, TrashSimpleIcon } from '@phosphor-icons/react'
import { Box, useMediaQuery } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { setInitialValues } from './form/setInitialValues'
import { usePreview } from '@pages/Preview/context'
import { returnPreviewModalSchema } from './form/validations'
import { getAccountValueFromAccountId, RETURN_PREVIEW_ACCOUNT_OPTIONS } from './constants/returnPreviewAccountOptions'

import type { AddRditOtherConsumptionsModalProps } from './types'

import * as S from './styles'

export const ReturnPreviousModal = ({
  data,
  open,
  onClose,
  returnHierarchy = 'hierarquia anterior',
  onSubmitSuccess,
  ...rest
}: AddRditOtherConsumptionsModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const {
    isReturnPreviewLoading,
    saveDraftReturnPreview,
    submitReturnPreview,
    isSavingDraftReturnPreview,
    getReturnPreview,
    deleteDraftReturnPreview,
    periodSelectedData
  } = usePreview()

  const methods = useForm<any>({
    defaultValues: initialFormValues,
    resolver: returnPreviewModalSchema
  })

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'accounts'
  })

  const [openCancelDetailedSummaryModal, setOpenCancelDetailedSummaryModal] = useState(false)
  const [openReturnSelectedModal, setOpenReturnSelectedModal] = useState(false)
  const [hasDraftSaved, setHasDraftSaved] = useState(false)
  const [hasUnsavedDraftChanges, setHasUnsavedDraftChanges] = useState(false)
  const initialDraftSnapshotRef = useRef<Map<string, { account: string; returnJustification: string }>>(new Map())

  const handleAddAccount = () => {
    append({ account: '', returnJustification: '', draftItemId: '' })
    setHasUnsavedDraftChanges(true)
  }

  const handleRemoveAccount = async (index: number) => {
    const draftItemId = methods.getValues(`accounts.${index}.draftItemId`) as string | undefined
    if (draftItemId?.trim()) {
      await deleteDraftReturnPreview([draftItemId.trim()])
      const branchCode = data?.profitCenter ?? ''
      const weekNumber = data?.weekNumber ?? 0
      const referenceDate = periodSelectedData?.competenceDate ?? ''
      if (branchCode && referenceDate) {
        const returnData = await getReturnPreview({ branchCode, weekNumber, referenceDate })
        const stillHasDraft = !!(returnData?.hasDraft && returnData.items?.length)
        setHasDraftSaved(stillHasDraft)
        initialDraftSnapshotRef.current = new Map(
          (returnData?.items ?? []).map((item) => [
            String(item.id),
            { account: getAccountValueFromAccountId(item.accountId), returnJustification: item.justification ?? '' }
          ])
        )
        setHasUnsavedDraftChanges(false)
      } else {
        setHasDraftSaved(false)
        initialDraftSnapshotRef.current = new Map()
        setHasUnsavedDraftChanges(false)
      }
    }
    remove(index)
  }

  const onError = (errors: FieldErrors<any>) => console.log({ errors })

  const onSaveDraft = async (formData: any) => {
    if (data) {
      const result = await saveDraftReturnPreview(data, formData)
      if (result) {
        setHasDraftSaved(true)
        setHasUnsavedDraftChanges(false)
        const branchCode = data.profitCenter ?? ''
        const weekNumber = data.weekNumber ?? 0
        const referenceDate = periodSelectedData?.competenceDate ?? ''
        if (branchCode && referenceDate) {
          const returnData = await getReturnPreview({ branchCode, weekNumber, referenceDate })
          initialDraftSnapshotRef.current = new Map(
            (returnData?.items ?? []).map((item) => [
              String(item.id),
              { account: getAccountValueFromAccountId(item.accountId), returnJustification: item.justification ?? '' }
            ])
          )
        }
      }
    }
  }

  const onSubmit = async () => {
    if (!data) return
    const branchCode = data.profitCenter ?? ''
    const weekNumber = Number(data.weekNumber) ?? 0
    const referenceDate = periodSelectedData?.competenceDate ?? ''
    if (!branchCode || !referenceDate) return
    const result = await submitReturnPreview({ branchCode, weekNumber, referenceDate })
    if (result) {
      await onSubmitSuccess?.()
      setOpenReturnSelectedModal(false)
      onClose(false)
      methods.reset(initialFormValues)
      setHasDraftSaved(false)
    }
  }

  const handleCloseModal = () => {
    if (hasDraftSaved && !hasUnsavedDraftChanges) {
      onClose(false)
      setHasDraftSaved(false)
      setHasUnsavedDraftChanges(false)
    } else {
      setOpenCancelDetailedSummaryModal(true)
    }
  }

  useEffect(() => {
    if (!data || !open) return

    const branchCode = data.profitCenter ?? ''
    const weekNumber = data.weekNumber ?? 0
    const referenceDate = periodSelectedData?.competenceDate ?? ''

    if (!branchCode || !referenceDate) {
      methods.reset(setInitialValues(data))
      setHasDraftSaved(false)
      return
    }

    getReturnPreview({ branchCode, weekNumber, referenceDate }).then((returnData) => {
      if (returnData?.hasDraft && returnData.items?.length) {
        methods.reset(setInitialValues(data, returnData.items))
        setHasDraftSaved(true)
        setHasUnsavedDraftChanges(false)
        initialDraftSnapshotRef.current = new Map(
          returnData.items.map((item) => [
            String(item.id),
            { account: getAccountValueFromAccountId(item.accountId), returnJustification: item.justification ?? '' }
          ])
        )
      } else {
        methods.reset(setInitialValues(data))
        setHasDraftSaved(false)
        setHasUnsavedDraftChanges(false)
        initialDraftSnapshotRef.current = new Map()
      }
    })
  }, [data, open])

  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
      setHasDraftSaved(false)
      setHasUnsavedDraftChanges(false)
      initialDraftSnapshotRef.current = new Map()
    }
  }, [open])

  useEffect(() => {
    const subscription = methods.watch((values) => {
      if (!hasDraftSaved) return
      const accounts = values?.accounts
      if (!Array.isArray(accounts) || accounts.length === 0) return
      const hasChange = accounts.some(
        (acc: { draftItemId?: string; account?: string; returnJustification?: string }) => {
          const id = typeof acc?.draftItemId === 'string' ? acc.draftItemId.trim() : ''
          if (!id) return true
          const initial = initialDraftSnapshotRef.current.get(id)
          if (!initial) return false
          return acc.account !== initial.account || (acc.returnJustification ?? '') !== initial.returnJustification
        }
      )
      setHasUnsavedDraftChanges(hasChange)
    })
    return () => subscription.unsubscribe()
  }, [methods, hasDraftSaved])

  return (
    <>
      <BaseModal2
        open={open}
        title="Devolver Prévia"
        headerIcon={<ArrowUUpLeftIcon />}
        onClose={handleCloseModal}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        footer={{
          cancelButtonText: hasDraftSaved && !hasUnsavedDraftChanges ? 'Fechar' : 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: hasDraftSaved && !hasUnsavedDraftChanges ? 'Enviar Devolução' : 'Salvar',
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: () => {
            if (hasDraftSaved && !hasUnsavedDraftChanges) {
              setOpenReturnSelectedModal(true)
            } else {
              methods.handleSubmit(onSaveDraft, onError)()
            }
          },
          loading: isSavingDraftReturnPreview
        }}
        {...rest}
      >
        <S.Title>Selecione a(s) Conta(s) e preencha o campo "Justificativa da devolução" antes de salvar.</S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={2}>
              <Input name="economicGroup" label="Grupo Econômico" disabled />
              <Input name="profitCenter" label="Centro de Lucro" disabled />
            </RowBox>

            <RowBox columns={3}>
              <Input name="budget" label="Budget GP (R$)" maskType="currency" placeholder="R$ 0,00" disabled />
              <Input
                name="actualPreview"
                label="Prévia Atual GP (R$)"
                maskType="currency"
                placeholder="R$ 0,00"
                disabled
              />
              <Input
                name="deviationVsBudget"
                label="Desvio vs Budget GP (R$)"
                maskType="currency"
                placeholder="R$ 0,00"
                disabled
              />
            </RowBox>

            {fields.map((field, index) => (
              <S.InputsBox key={field.id}>
                <input type="hidden" {...methods.register(`accounts.${index}.draftItemId`)} />
                <RowBox columns={3}>
                  <Select
                    required
                    name={`accounts.${index}.account`}
                    label="Conta"
                    placeholder="Selecione a conta"
                    options={[...RETURN_PREVIEW_ACCOUNT_OPTIONS]}
                  />
                  <Box sx={{ gridColumn: 'span 2' }}>
                    <Input required name={`accounts.${index}.returnJustification`} label="Justificativa da devolução" />
                  </Box>
                </RowBox>

                <S.AddAccountButton onClick={handleAddAccount}>
                  <PlusCircleIcon />
                </S.AddAccountButton>

                {fields.length > 1 && (
                  <S.AddAccountButton onClick={() => handleRemoveAccount(index)}>
                    <TrashSimpleIcon />
                  </S.AddAccountButton>
                )}
              </S.InputsBox>
            ))}
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelDetailedSummaryModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelDetailedSummaryModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelDetailedSummaryModal(false)
        }}
      />

      <ReturnModal
        open={openReturnSelectedModal}
        headerIcon={<ArrowUUpLeftIcon size={24} weight="bold" />}
        headerTitle="Devolver Prévia"
        message={`Tem certeza de que deseja devolver a Prévia para ajuste do(a) ${returnHierarchy}?`}
        warningMessage="Após confirmação, esta ação não poderá ser desfeita."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReturnSelectedModal}
        isLoading={isReturnPreviewLoading}
        onConfirm={() => methods.handleSubmit(onSubmit, onError)()}
      />
    </>
  )
}
